import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, CheckCircle, Wrench, Shield, Clock, Star, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Header } from './Header';
import { Footer } from './Footer';
import { WhatsAppButton } from './WhatsAppButton';
import { CallButton } from './CallButton';
import { TopAnnouncementBar } from './TopAnnouncementBar';

export const BmwRepair = () => {
  const bmwServices = [
    {
      title: "Engine Diagnostics & Repair",
      description: "Advanced BMW engine diagnostics using specialized tools for accurate problem detection",
      icon: Wrench
    },
    {
      title: "Transmission Service",
      description: "Expert BMW automatic and manual transmission repair and maintenance",
      icon: Wrench
    },
    {
      title: "Brake System Service",
      description: "Complete BMW brake inspection, pad replacement, and rotor servicing",
      icon: Shield
    },
    {
      title: "Electrical Systems",
      description: "BMW electrical diagnostics and repair for all electronic components",
      icon: Wrench
    },
    {
      title: "Suspension & Steering",
      description: "BMW suspension repair and wheel alignment for optimal handling",
      icon: Wrench
    },
    {
      title: "Oil Change & Maintenance",
      description: "BMW-specific oil changes and routine maintenance services",
      icon: Clock
    },
    {
      title: "AC System Repair",
      description: "BMW air conditioning service and refrigerant recharge",
      icon: Wrench
    },
    {
      title: "Battery & Alternator",
      description: "BMW battery replacement and charging system diagnostics",
      icon: Wrench
    }
  ];

  const bmwModels = [
    "BMW 1 Series", "BMW 2 Series", "BMW 3 Series", "BMW 4 Series",
    "BMW 5 Series", "BMW 6 Series", "BMW 7 Series", "BMW 8 Series",
    "BMW X1", "BMW X2", "BMW X3", "BMW X4", "BMW X5", "BMW X6", "BMW X7",
    "BMW M Series (M3, M4, M5, M6, M8)", "BMW Z4", "BMW i3", "BMW i8"
  ];

  const whyChooseUs = [
    {
      title: "BMW-Certified Technicians",
      description: "Our mechanics are specially trained and certified for BMW repairs"
    },
    {
      title: "Genuine Parts Available",
      description: "We use OEM and genuine BMW parts for all repairs and replacements"
    },
    {
      title: "Advanced Diagnostic Tools",
      description: "State-of-the-art BMW diagnostic equipment for precise problem identification"
    },
    {
      title: "Warranty on Services",
      description: "All BMW repairs come with comprehensive warranty coverage"
    }
  ];

  const openWhatsApp = () => {
    const message = "Hi Think Mechanic, I need BMW repair service and want to claim my 15% website discount!";
    const whatsappNumber = '+971566671468';
    const whatsappUrl = `https://wa.me/${whatsappNumber.replace(/\s/g, '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>BMW Repair Dubai | BMW Engine, AC & Brake Repair | Think Mechanic</title>
        <meta name="description" content="Professional BMW repair in Dubai including engine diagnostics, AC repair, brake service, transmission and electrical repairs at Think Mechanic Ras Al Khor." />
        <link rel="canonical" href="https://thinkmechanicdxb.com/bmw-repair-dubai" />
        <meta name="keywords" content="BMW repair Dubai, BMW service Dubai, BMW garage Dubai, BMW workshop Dubai, BMW mechanic Dubai, BMW specialist Dubai, BMW garage near me, BMW mechanic near me, BMW workshop Ras Al Khor, BMW garage Ras Al Khor, BMW AC repair Dubai, BMW oil change Dubai, BMW brake repair Dubai, BMW engine repair Dubai, BMW transmission repair Dubai, BMW maintenance Dubai, BMW diagnostic Dubai, BMW repair near me, Best BMW garage Dubai, BMW service center Dubai, Affordable BMW repair Dubai, Cheap BMW service Dubai, BMW oil change near me, BMW brake service Dubai, BMW suspension repair Dubai, BMW electrical repair Dubai, BMW battery replacement Dubai, BMW tire service Dubai, BMW wheel alignment Dubai, Think Mechanic BMW, BMW 3 series repair, BMW 5 series repair, BMW X5 repair Dubai, BMW X3 service Dubai, BMW repair Ras Al Khor, Emergency BMW repair Dubai, Same day BMW service, BMW cooling system repair, BMW check engine light Dubai, Certified BMW technician Dubai, Independent BMW garage Dubai, Trusted BMW mechanic Dubai, BMW M series repair, BMW 320i service Dubai, BMW 520i repair Dubai, BMW iDrive repair, Out of warranty BMW repair, BMW extended warranty Dubai, BMW pre-purchase inspection, Used BMW inspection Dubai, Top BMW garage Dubai, BMW service Al Quoz, BMW repair Sharjah, car AC repair Dubai, car AC gas refill Dubai, auto AC repair near me" />
        
        {/* Open Graph */}
        <meta property="og:title" content="BMW Repair Dubai | Expert BMW Service | Think Mechanic" />
        <meta property="og:description" content="Professional BMW repair in Dubai. Certified technicians, genuine parts, 15+ years experience. All BMW models serviced." />
        <meta property="og:url" content="https://thinkmechanicdxb.com/bmw-repair-dubai" />
        <meta property="og:type" content="website" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="BMW Repair Dubai | Think Mechanic" />
        <meta name="twitter:description" content="Expert BMW repair and maintenance in Dubai. Certified technicians, genuine parts." />
      </Helmet>

      {/* Top Announcement Bar */}
      <TopAnnouncementBar />

      {/* Same Header as Homepage */}
      <Header />

      {/* Hero Section with BMW Image Background */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* BMW Background Image - BMW car in professional workshop/garage */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1702939420383-7de8995e708b?w=1920&q=85"
            alt="BMW Car in Professional Workshop - Think Mechanic Dubai"
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center center' }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center py-32">
            {/* BMW Logo Badge - Using your uploaded logo */}
            <div className="inline-flex items-center justify-center space-x-3 bg-white/10 backdrop-blur-md border-2 border-white/20 px-6 py-3 rounded-full mb-6">
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center p-1">
                <img 
                  src="https://customer-assets.emergentagent.com/job_think-mechanic/artifacts/km2aeqcj_image.png"
                  alt="BMW Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="flex flex-col items-start">
                <span className="text-white font-bold text-lg">BMW</span>
                <span className="text-white/80 text-xs font-semibold">Repair Specialists</span>
              </div>
            </div>

            {/* Main Heading */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Expert BMW Repair in
              <span className="text-red-600"> Dubai</span>
            </h1>

            {/* Subheading */}
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Specialized BMW repair and maintenance services in Ras Al Khor. Trust certified technicians with your ultimate driving machine.
            </p>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                onClick={openWhatsApp}
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 rounded-lg text-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-red-600/50 group"
              >
                Book BMW Service & Get 15% OFF
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <p className="text-white/90 text-sm">✨ Special discount for website visitors</p>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-6 mt-16 max-w-2xl mx-auto">
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600 mb-2">15+</div>
                <div className="text-gray-300 text-sm">Years Experience</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600 mb-2">500+</div>
                <div className="text-gray-300 text-sm">BMWs Serviced</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600 mb-2">100%</div>
                <div className="text-gray-300 text-sm">Satisfaction</div>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative Element */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent z-10"></div>
      </section>

      {/* BMW Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">Our Services</span>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mt-3 mb-4">
              Comprehensive BMW Repair Services
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              From routine maintenance to complex repairs, we handle all BMW service needs with expertise and precision.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {bmwServices.map((service, index) => (
              <Card key={index} className="bg-white border-gray-200 p-6 hover:border-red-600 transition-all duration-300">
                <div className="bg-red-600/20 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <service.icon className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-gray-900 font-bold text-lg mb-2">{service.title}</h3>
                <p className="text-gray-600 text-sm">{service.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Why Choose Think Mechanic for BMW Repair?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {whyChooseUs.map((reason, index) => (
              <div key={index} className="flex items-start space-x-4">
                <div className="bg-red-600 rounded-full p-2 flex-shrink-0">
                  <CheckCircle className="w-6 h-6 text-gray-900" />
                </div>
                <div>
                  <h3 className="text-gray-900 font-bold text-xl mb-2">{reason.title}</h3>
                  <p className="text-gray-600">{reason.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BMW Models We Service */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              BMW Models We Service
            </h2>
            <p className="text-gray-600 text-lg">
              We service all BMW models, from classic to latest
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {bmwModels.map((model, index) => (
              <div key={index} className="bg-white border border-gray-200 px-4 py-3 rounded-lg text-center hover:border-red-600 transition-all duration-300">
                <span className="text-gray-900 font-semibold">{model}</span>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <p className="text-gray-600">+ All other BMW models</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-red-600 to-red-700">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Ready to Service Your BMW?
          </h2>
          <p className="text-white/90 text-xl mb-8 max-w-2xl mx-auto">
            Book your BMW service today and get 15% off. Our expert technicians are ready to help!
          </p>
          
          <Button
            onClick={openWhatsApp}
            className="bg-white hover:bg-gray-100 text-red-600 px-10 py-6 rounded-lg text-xl font-bold transition-all duration-200 shadow-2xl hover:scale-105"
          >
            Book Now via WhatsApp
            <ArrowRight className="ml-2 w-6 h-6" />
          </Button>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-6 text-gray-900">
            <div className="flex items-center space-x-2">
              <Phone className="w-5 h-5" />
              <span className="font-semibold">056 667 1468</span>
            </div>
            <div className="hidden sm:block w-px h-6 bg-white/30"></div>
            <div>
              <span>📍 Ras Al Khor Industrial Area 1, Dubai</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
      
      {/* Call Button */}
      <CallButton />
      
      {/* WhatsApp Button */}
      <WhatsAppButton />
    </div>
  );
};
